import useBanks from '@/hooks/useBanks';
import type { BanksResponse, IdAndName } from '@/models/common/BankData';

export interface BankDirectory {
  bankCodeFromText(text: string): number | null;
  branchNumberFromText(bankCode: number, text: string): string | null;
}

// A value pasted from an external workbook arrives in whatever shape the source used:
// the template's own "12 - בנק הפועלים", a bare code, or only the bank's name.
const CODE_WITH_OPTIONAL_LABEL = /^(\d{1,5})(?:\s*[-–—]\s*.*)?$/s;

export function parseLeadingCode(value: string): number | null {
  const match = CODE_WITH_OPTIONAL_LABEL.exec(value.trim());
  if (!match) return null;
  const code = Number(match[1]);
  return code > 0 ? code : null;
}

const QUOTE_CHARS = /["'״׳“”‘’`]/g;

function normalizeName(value: string): string {
  return value
    .replace(QUOTE_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s*בעמ$/, '')
    .trim()
    .toLowerCase();
}

const MIN_PARTIAL_MATCH_LENGTH = 3;

// A partial match is only accepted when it is unambiguous: a name that fits two banks is
// left unresolved rather than guessed at, so the row fails loudly instead of paying the
// wrong account.
function findUniqueByName<T>(entries: T[], nameOf: (entry: T) => string, text: string): T | null {
  const target = normalizeName(text);
  if (!target) return null;

  const named = entries.map((entry) => ({ entry, name: normalizeName(nameOf(entry)) }));

  const exact = named.filter((candidate) => candidate.name === target);
  if (exact.length > 0) {
    return exact.length === 1 ? exact[0].entry : null;
  }

  if (target.length < MIN_PARTIAL_MATCH_LENGTH) return null;
  const partial = named.filter(
    (candidate) =>
      candidate.name.length >= MIN_PARTIAL_MATCH_LENGTH &&
      (candidate.name.includes(target) || target.includes(candidate.name))
  );
  return partial.length === 1 ? partial[0].entry : null;
}

interface BankEntry {
  code: number;
  name: string;
  branches: IdAndName[];
}

export function createBankDirectory(banks: BanksResponse | null | undefined): BankDirectory {
  const entries: BankEntry[] = Object.entries(banks ?? {})
    .map(([code, bank]) => ({
      code: Number(code),
      name: bank?.name ?? '',
      branches: bank?.branches ?? [],
    }))
    .filter((entry) => Number.isInteger(entry.code) && entry.code > 0);

  const byCode = new Map(entries.map((entry) => [entry.code, entry]));

  return {
    bankCodeFromText(text: string): number | null {
      const code = parseLeadingCode(text);
      if (code != null) return code;

      return findUniqueByName(entries, (entry) => entry.name, text)?.code ?? null;
    },

    branchNumberFromText(bankCode: number, text: string): string | null {
      const branchNumber = parseLeadingCode(text);
      if (branchNumber != null) return String(branchNumber);

      const branches = byCode.get(bankCode)?.branches ?? [];
      const branch = findUniqueByName(branches, (entry) => entry.name, text);
      return branch ? String(branch.id) : null;
    },
  };
}

// Resolves codes written as plain numbers only — the fallback when the bank list is unavailable.
export const EMPTY_BANK_DIRECTORY: BankDirectory = createBankDirectory({});

export async function loadBankDirectory(): Promise<BankDirectory> {
  try {
    return createBankDirectory(await useBanks.getBanks());
  } catch {
    // Names cannot be matched without the list, but codes typed as numbers still resolve.
    return EMPTY_BANK_DIRECTORY;
  }
}
